import { MapStringTo, Nullable } from "../../base-types";
import { NOTIFICATIONS } from "../../constants";
import { NotificationLevel } from "./notification-level";

/**
 * Interface for real-time notification requests.
 */
export interface IRealtimeRequest {
    /**
     * Gets or sets the title of the notification.
     */
    title: string;

    /**
     * Gets or sets the target when the notification is clicked.
     */
    link: string; //! Note: Link has Uri type normally, might be added if necessary

    /**
     * Gets or sets the notification level.
     */
    notificationLevel: NotificationLevel;

    /**
     * Gets or sets the notification options. This directly maps to the notification API standard used by browsers.
     * See https://developer.mozilla.org/en-US/docs/Web/API/notification for more information.
     */
    options: MapStringTo<unknown>;

    /**
     * Gets the notification body from the options object.
     */
    getBody(): Nullable<string>;

    /**
     * Sets the notification body on the options object.
     * @param body - The notification body
     */
    setBody(body: string): void;

    /**
     * Gets the notification icon from the options object.
     */
    getIcon(): Nullable<string>;

    /**
     * Sets the notification icon on the options object.
     * @param icon - The notification icon
     */
    setIcon(icon: string): void;

    /**
     * Validates the real-time request.
     */
    validate(): void;
}

/**
 * Base class for real-time notification (browser notification) requests.
 */
export abstract class RealtimeRequest implements IRealtimeRequest {
    title: string;

    link: string;

    notificationLevel: NotificationLevel = NotificationLevel.Info;

    options: MapStringTo<unknown> = {};

    getBody(): Nullable<string> {
        if (this.options == null || !(NOTIFICATIONS.REALTIME.INFO.body in this.options)) {
            return null;
        }

        const body = this.options[NOTIFICATIONS.REALTIME.INFO.body] as string;

        return body;
    }

    setBody(body: string): void {
        if (this.options == null) {
            this.options = {};
        }

        this.options[NOTIFICATIONS.REALTIME.INFO.body] = body;
    }

    getIcon(): Nullable<string> {
        if (this.options == null || !(NOTIFICATIONS.REALTIME.INFO.body in this.options)) {
            return null;
        }

        const icon = this.options[NOTIFICATIONS.REALTIME.INFO.icon] as string;

        return icon;
    }

    setIcon(icon: string): void {
        if (this.options == null) {
            this.options = {};
        }

        this.options[NOTIFICATIONS.REALTIME.INFO.icon] = icon;
    }

    abstract validate(): void;
}
